import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition duration-150 ease-soft active:scale-[0.98] motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:shadow-ring",
  {
    variants: {
      variant: {
        default: "bg-neutral-950 text-white hover:bg-neutral-800 disabled:opacity-40",
        outline:
          "border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300",
        ghost: "text-neutral-500 hover:bg-neutral-50 hover:text-neutral-950",
        link: "text-brand font-medium text-[13px] hover:underline",
      },
      size: {
        default: "h-10 w-full rounded-[10px]",
        sm: "h-8 px-3.5 rounded-lg text-[13px]",
        icon: "h-11 w-7 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
